import React, { createContext, useCallback, useContext, useMemo } from 'react';
import useSWR, {
  unstable_serialize,
  useSWRConfig,
  type SWRConfiguration,
} from 'swr';
import { getKombo } from 'api/generated/api/kombo';
import type { AtsIntegrationResponse } from 'api/generated/types/atsIntegrationResponse';
import { referenceDataSwrOptions } from 'api/swrConfig';
import { atsIntegrationsKey } from 'hooks/orgKeys';
import { writeAtsIntegrationsCache } from 'utils/atsCoverage';
import { getValidOrgId } from 'utils/storageUtils';
import { getDefaultAtsIntegration } from 'utils/atsIntegrationSelection';
import { notifyError } from 'utils/notify';
import { appText } from 'utils/appLocale';
import { shellDomainText } from 'utils/domainTextFallback';
import { useOrgId } from 'hooks/useUserSession';

const fetchAtsIntegrations = async (): Promise<AtsIntegration[]> =>
  (await getKombo().komboHandleKomboGetAtsIntegrations()) || [];

export type AtsIntegration = AtsIntegrationResponse;

/** Shared list for consumers that also render outside an ATS provider. */
export const useAtsIntegrationsResource = (
  enabled = true,
  options: Pick<SWRConfiguration, 'revalidateOnMount'> = {}
) => {
  // SWR dedupes by key across providers and standalone consumers, so they
  // reuse one in-flight request and one cache entry. The key includes org so
  // a same-tab login to another org cannot reuse the previous org's list.
  const orgId = useOrgId();
  const key = enabled ? atsIntegrationsKey(orgId) : null;
  const serializedKey = key ? unstable_serialize(key) : null;
  const { cache } = useSWRConfig();
  const resource = useSWR<AtsIntegration[], unknown>(
    key,
    fetchAtsIntegrations,
    {
      ...referenceDataSwrOptions,
      ...options,
      // Match the original single-toast behavior: without this, SWR retries on
      // error and onError fires (and toasts) on each retry attempt.
      shouldRetryOnError: false,
      onSuccess: (integrations) => {
        // The mirror is a single unscoped key. Skip a late write from a
        // previous org's in-flight request after logout or same-tab login.
        if (getValidOrgId() !== orgId) {
          return;
        }
        writeAtsIntegrationsCache(integrations);
      },
      onError: (err) => {
        console.error('Error fetching ATS integrations:', err);
        notifyError(
          appText('status.error', 'Error'),
          shellDomainText('atsIntegrations.fetchFailed')
        );
      },
    }
  );

  // Mount revalidation may start after effects; cached-error middleware starts
  // it before consumer effects. Check both the render flag and current cache.
  const isValidating = resource.isValidating;
  const isPending = useCallback(() => {
    return Boolean(
      serializedKey && (isValidating || cache.get(serializedKey)?.isValidating)
    );
  }, [cache, serializedKey, isValidating]);
  const readError = useCallback(
    (): unknown =>
      serializedKey ? cache.get(serializedKey)?.error : undefined,
    [cache, serializedKey]
  );

  return { ...resource, isPending, readError };
};

interface AtsIntegrationsState {
  atsIntegrations: AtsIntegration[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getFirstIntegrationId: () => string | null;
}

const AtsIntegrationsContext = createContext<AtsIntegrationsState | undefined>(
  undefined
);

interface AtsIntegrationsProviderProps {
  children: React.ReactNode;
}

/**
 * Fetches ATS integrations and provides context. Only used when there is no
 * ancestor AtsIntegrationsProvider (see AtsIntegrationsProvider wrapper).
 */
const AtsIntegrationsFetcherProvider: React.FC<
  AtsIntegrationsProviderProps
> = ({ children }) => {
  const {
    data: atsIntegrations = [],
    error: swrError,
    isLoading,
    mutate,
  } = useAtsIntegrationsResource();

  const refetch = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const getFirstIntegrationId = useCallback(() => {
    return (
      getDefaultAtsIntegration(atsIntegrations)?.ats_integration_id ?? null
    );
  }, [atsIntegrations]);

  const contextValue = useMemo(
    () => ({
      atsIntegrations,
      loading: isLoading,
      error: swrError ? shellDomainText('atsIntegrations.fetchFailed') : null,
      refetch,
      getFirstIntegrationId,
    }),
    [atsIntegrations, isLoading, swrError, refetch, getFirstIntegrationId]
  );

  return (
    <AtsIntegrationsContext.Provider value={contextValue}>
      {children}
    </AtsIntegrationsContext.Provider>
  );
};

/**
 * Provides ATS integration list for the org. Nesting this provider under an
 * existing one does not run a second fetch — children reuse the ancestor
 * context (e.g. NewJobModal inside ScriptTabsPage).
 */
export const AtsIntegrationsProvider: React.FC<
  AtsIntegrationsProviderProps
> = ({ children }) => {
  const parentContext = useContext(AtsIntegrationsContext);
  if (parentContext !== undefined) {
    return <>{children}</>;
  }
  return (
    <AtsIntegrationsFetcherProvider>{children}</AtsIntegrationsFetcherProvider>
  );
};

export const useAtsIntegrations = () => {
  const context = useContext(AtsIntegrationsContext);
  if (context === undefined) {
    throw new Error(
      'useAtsIntegrations must be used within an AtsIntegrationsProvider'
    );
  }
  return context;
};
